'use client';

import { createContext, useContext } from 'react';
import { resolveOrgLabels, type OrgLabels } from '@/lib/org-labels';

export type { OrgLabels } from '@/lib/org-labels';
export { resolveOrgLabels } from '@/lib/org-labels';

const UNKNOWN_LABELS: OrgLabels = resolveOrgLabels(undefined, false);

const OrgLabelsContext = createContext<OrgLabels>(UNKNOWN_LABELS);

export const OrgLabelsProvider = OrgLabelsContext.Provider;

/**
 * localStorage cache for the resolved orgType. Used by (dashboard)/layout.tsx
 * to avoid the "residential flash" — the brief moment after navigation when
 * tenantConfig is still loading and the layout's useState defaults to
 * a guessed label set, causing tenants to see the wrong program vocabulary
 * before the async fetch updates them. Caching the previously-resolved
 * orgType means returning users get the right labels on first paint.
 *
 * Cache is cleared on logout via the auth signout flow.
 */
const ORG_TYPE_CACHE_KEY = 'statura.orgType';

export function getCachedOrgType(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage.getItem(ORG_TYPE_CACHE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function setCachedOrgType(orgType: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (orgType) {
      window.localStorage.setItem(ORG_TYPE_CACHE_KEY, orgType);
    } else {
      window.localStorage.removeItem(ORG_TYPE_CACHE_KEY);
    }
  } catch {
    // localStorage may be unavailable (private browsing, quota, etc.)
  }
}

export function clearCachedOrgType(): void {
  setCachedOrgType(null);
}

/** Access organisation-type-aware labels (Client vs Resident, Office vs Facility, etc.) */
export function useOrgLabels(): OrgLabels {
  return useContext(OrgLabelsContext);
}
